import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Switch, TextField, Typography } from '@mui/material';
import AgentHubService from 'services/AgentHubService';
import { AgentState, RunField, SavedRunSettings } from './types';
import { describeSchedule, NO_SCHEDULE, scheduleProblem } from './scheduleModel';
import { fmtTime } from './parts';
import ScheduleEditor from './ScheduleEditor';

const service = new AgentHubService();
// "Daily at 7:00 AM PT" mid-sentence: only the first letter lowered, so AM/PM/PT stay as they are.
const lowerFirst = (t: string) => t.charAt(0).toLowerCase() + t.slice(1);
type Change = Partial<Record<RunField, string | boolean | number | null>>;

function Row({ label, children, source }: { label: string; children: React.ReactNode; source: React.ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '110px 1fr' }, gap: 1, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, pt: 0.75 }}>{label}</Typography>
      <Box>
        {children}
        <Box sx={{ mt: 0.25 }}>{source}</Box>
      </Box>
    </Box>
  );
}

/**
 * When the agent runs: Active or Draft, paused or not (the same pause as the Pause button), its schedule, whether it
 * takes tasks, its priority. agent.yaml sets each (it never pauses); a value
 * saved here overrides it until it's reset, and the hub applies it as soon as it's saved (no deploy, no PR).
 */
export default function RunSettings({ agent }: { agent: AgentState }) {
  const [saved, setSaved] = useState<SavedRunSettings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState<RunField | null>(null);
  const [schedule, setSchedule] = useState('');
  const [priority, setPriority] = useState('');
  const [confirmStatus, setConfirmStatus] = useState<'active' | 'draft' | null>(null);
  const src = agent.run;

  const yamlSchedule = src?.yaml.schedule ?? NO_SCHEDULE;
  const eff = (s: SavedRunSettings | null) => ({
    status: s?.status ?? src?.yaml.status ?? agent.status,
    schedule: s?.schedule ?? yamlSchedule,
    onDemand: s?.onDemand ?? src?.yaml.onDemand ?? agent.onDemand,
    priority: s?.priority ?? src?.yaml.priority ?? agent.priority,
  });
  const show = (s: SavedRunSettings) => { setSaved(s); const e = eff(s); setSchedule(e.schedule); setPriority(String(e.priority)); };

  useEffect(() => {
    let live = true;
    setSaved(null);
    setLoadError(null);
    setSaveError(null);
    service.settings(agent.name).then(s => live && show(s.run ?? {})).catch(e => live && setLoadError(`Run settings not loaded: ${e.message}`));
    return () => { live = false; };
  }, [agent.name, agent.paused]); // eslint-disable-line react-hooks/exhaustive-deps -- a Pause / Resume button elsewhere on the page changes the saved pause

  if (!src) {
    return <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>When it runs is set in its agent.yaml until the hub runs a version that reads it from this page.</Typography>;
  }
  if (src.yaml.status === 'retired') {
    return <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2 }}>Retired in its agent.yaml: it doesn't run, and its run settings can't be changed here.</Typography>;
  }

  const save = (field: RunField, change: Change) => {
    setSaving(field);
    setSaveError(null);
    service.saveRunSettings(agent.name, change)
      .then(s => show(s.run ?? {}))
      .catch(e => setSaveError(`Not saved: ${e.message}`))
      .finally(() => setSaving(null));
  };

  const e = eff(saved);
  // Which source a field shows: saved here (by whom, when) or agent.yaml's, with a reset back to it.
  const source = (field: RunField, yamlText: string) => {
    if (!saved || saved[field] === undefined) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>From agent.yaml</Typography>;
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Set on this page{saved.by ? ` by ${saved.by}` : ''}{saved.at ? ` on ${fmtTime(saved.at)}` : ''}. agent.yaml says {yamlText}.
        <Button size="small" sx={{ ml: 1, py: 0, minWidth: 0 }} disabled={!!saving} onClick={() => save(field, { [field]: null })}>Reset to agent.yaml</Button>
      </Typography>
    );
  };

  const prio = Number(priority);
  const prioBad = !/^\d+$/.test(priority.trim()) || prio > 100;
  const scheduleChanged = !!saved && schedule.trim() !== e.schedule;
  const lastStartedAt = agent.lastRun?.startedAt ? new Date(agent.lastRun.startedAt) : null;
  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>When it runs</Typography>
      {loadError && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}
      {src.problems.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>The hub couldn't use what's saved here and is running on agent.yaml for it: {src.problems.join('; ')}</Alert>
      )}
      {!saved && !loadError && <CircularProgress size={18} />}
      {saved && (
        <>
          <Row label="Status" source={source('status', src.yaml.status === 'active' ? 'Active' : 'Draft')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch size="small" checked={e.status === 'active'} disabled={!!saving} onChange={ev => setConfirmStatus(ev.target.checked ? 'active' : 'draft')} />
              <Typography variant="body2">{e.status === 'active' ? 'Active' : 'Draft: no scheduled runs'}</Typography>
              {saving === 'status' && <CircularProgress size={14} />}
            </Box>
          </Row>

          <Row label="Paused" source={source('paused', 'not paused')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch size="small" checked={saved.paused === true} disabled={!!saving} onChange={ev => save('paused', { paused: ev.target.checked ? true : null })} />
              <Typography variant="body2">{saved.paused ? 'Paused: starts nothing new until resumed; a run already going finishes' : 'Not paused'}</Typography>
              {saving === 'paused' && <CircularProgress size={14} />}
            </Box>
          </Row>

          <Row label="Schedule" source={source('schedule', lowerFirst(describeSchedule(yamlSchedule)))}>
            <ScheduleEditor value={schedule} onChange={setSchedule} lastStartedAt={lastStartedAt} disabled={!!saving} />
            {scheduleChanged && (
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button size="small" variant="contained" disabled={!!saving || !!scheduleProblem(schedule)} onClick={() => save('schedule', { schedule: schedule.trim() })}>{saving === 'schedule' ? 'Saving…' : 'Save schedule'}</Button>
                <Button size="small" disabled={!!saving} onClick={() => setSchedule(e.schedule)}>Undo</Button>
              </Box>
            )}
            {e.status === 'draft' && e.schedule !== NO_SCHEDULE && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>It's a draft, so this schedule doesn't run until it's set Active.</Typography>}
          </Row>

          <Row label="Takes tasks" source={source('onDemand', src.yaml.onDemand ? 'yes' : 'no')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch size="small" checked={e.onDemand} disabled={!!saving} onChange={ev => save('onDemand', { onDemand: ev.target.checked })} />
              <Typography variant="body2">{e.onDemand ? 'Yes: people, the API and other agents can give it work' : 'No: only its schedule runs it'}</Typography>
              {saving === 'onDemand' && <CircularProgress size={14} />}
            </Box>
          </Row>

          <Row label="Priority" source={source('priority', String(src.yaml.priority))}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField size="small" value={priority} disabled={!!saving} onChange={ev => setPriority(ev.target.value)} error={prioBad} sx={{ width: 90 }} inputProps={{ inputMode: 'numeric' }} />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>0 to 100; higher starts first when runs are waiting for a slot</Typography>
              {!prioBad && prio !== e.priority && (
                <>
                  <Button size="small" variant="contained" disabled={!!saving} onClick={() => save('priority', { priority: prio })}>{saving === 'priority' ? 'Saving…' : 'Save'}</Button>
                  <Button size="small" disabled={!!saving} onClick={() => setPriority(String(e.priority))}>Undo</Button>
                </>
              )}
            </Box>
          </Row>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Saved here, the hub applies it within seconds; no deploy or pull request.</Typography>
        </>
      )}

      <Dialog open={!!confirmStatus} onClose={() => setConfirmStatus(null)}>
        <DialogTitle>{confirmStatus === 'draft' ? `Set ${agent.name} to Draft?` : `Set ${agent.name} to Active?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmStatus === 'draft'
              ? `Its scheduled runs stop until it's set Active again.${e.onDemand ? ' Tasks it is given still run.' : ''} A run going now finishes.`
              : `It starts running on its schedule: ${lowerFirst(describeSchedule(e.schedule))}.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatus(null)}>Cancel</Button>
          <Button variant="contained" onClick={() => { const s = confirmStatus!; setConfirmStatus(null); save('status', { status: s }); }}>{confirmStatus === 'draft' ? 'Set to Draft' : 'Set to Active'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
